import { useEffect, useState } from 'react';
import { ApiError, createCheckout, getPaymentProvider, listOfferings } from './api/client';
import type { OfferingResponse, PaymentProviderResponse, StorefrontTenantResponse } from './api/types';
import { navigate } from './auth/redirect';
import { explainError } from './purchase/errors';
import type { ErrorExplanation } from './purchase/errors';
import { storePendingPurchase } from './purchase/pendingPurchase';

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

/** The live payment-provider relay for the tenant — shown as-is, including its error answers. */
type RelayState =
  | { kind: 'loading' }
  | { kind: 'ok'; provider: PaymentProviderResponse }
  | { kind: 'error'; explanation: ErrorExplanation }
  | { kind: 'failed' };

/** A checkout attempt that did not redirect — a domain answer to explain, or a plain failure. */
type CheckoutOutcome = { kind: 'error'; explanation: ErrorExplanation } | { kind: 'failed' };

const left = { textAlign: 'left' } as const;

/**
 * One shop window: the selected test tenant's catalog, its live provider relay result, and a
 * Buy per offering that runs the real checkout path. Buy stays enabled on store-billed tenants
 * on purpose — seeing their 409 is what this storefront is for.
 */
export function TenantShop({ tenant }: { tenant: StorefrontTenantResponse }) {
  const [offerings, setOfferings] = useState<OfferingResponse[] | null>(null);
  const [relay, setRelay] = useState<RelayState>({ kind: 'loading' });
  const [outcome, setOutcome] = useState<CheckoutOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reload catalog + relay whenever the selected shop window changes.
  useEffect(() => {
    setOutcome(null);
    setError(null);
    setOfferings(null);
    setRelay({ kind: 'loading' });
    void listOfferings(tenant.tenantId)
      .then(setOfferings)
      .catch((e: unknown) => setError(describeError(e)));
    void getPaymentProvider(tenant.tenantId)
      .then((provider) => setRelay({ kind: 'ok', provider }))
      .catch((e: unknown) =>
        setRelay(
          e instanceof ApiError
            ? { kind: 'error', explanation: explainError(e.body) }
            : { kind: 'failed' },
        ),
      );
  }, [tenant.tenantId]);

  async function buy(accessId: string): Promise<void> {
    setBusy(true);
    setOutcome(null);
    try {
      const checkout = await createCheckout(tenant.tenantId, {
        accessId,
        successUrl: `${window.location.origin}/purchase/success`,
        cancelUrl: `${window.location.origin}/purchase/cancelled`,
      });
      // Ride the redirect: the success page reads this back after the provider round-trip.
      storePendingPurchase({
        tenantId: tenant.tenantId,
        accessId,
        provider: checkout.provider,
        sessionId: checkout.sessionId,
      });
      navigate(checkout.redirectUrl);
    } catch (e: unknown) {
      setOutcome(
        e instanceof ApiError
          ? { kind: 'error', explanation: explainError(e.body) }
          : { kind: 'failed' },
      );
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>{tenant.displayName}</h2>

      <p>
        Live provider relay:{' '}
        {relay.kind === 'loading' ? (
          <em>loading…</em>
        ) : relay.kind === 'ok' ? (
          <span>
            <strong>{relay.provider.provider}</strong>{' '}
            {relay.provider.enabled ? '(enabled)' : '(disabled)'}
          </span>
        ) : relay.kind === 'error' ? (
          <span>
            {relay.explanation.explanation} <code>{relay.explanation.code}</code>
          </span>
        ) : (
          <span>relay request failed</span>
        )}
      </p>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      {offerings === null && error === null ? (
        <p>Loading offerings…</p>
      ) : offerings !== null ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={left}>Access ID</th>
              <th style={left}>Period</th>
              <th style={left}>Price</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {offerings.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <em>No offerings for &ldquo;{tenant.tenantId}&rdquo;.</em>
                </td>
              </tr>
            ) : (
              offerings.map((o) => (
                <tr key={o.accessId}>
                  <td>{o.accessId}</td>
                  <td>
                    {o.period.amount} {o.period.unit}
                  </td>
                  <td>
                    {o.price.amount} {o.price.currency}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => void buy(o.accessId)} disabled={busy}>
                      Buy
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : null}

      {outcome !== null && (
        <div role="alert" style={{ marginTop: '1rem' }}>
          {outcome.kind === 'failed' ? (
            <p style={{ color: '#b00020' }}>Checkout request failed.</p>
          ) : (
            <>
              <p style={{ color: outcome.explanation.expected ? '#0a7d33' : '#b00020' }}>
                <strong>
                  {outcome.explanation.expected
                    ? 'Expected platform behavior'
                    : 'Checkout did not start'}
                </strong>
              </p>
              <p>{outcome.explanation.explanation}</p>
              <p>
                <code>{outcome.explanation.code}</code>
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
